import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search as SearchIcon, FileText, Folder, User, Loader2 } from 'lucide-react';
import { apiClient } from '@/services/api/axiosClient';

export function GlobalSearch() {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<any>({ documents: [], folders: [], users: [] });
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.trim()) {
        performSearch(query);
      } else {
        setResults({ documents: [], folders: [], users: [] });
        setIsOpen(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const performSearch = async (q: string) => {
    setIsLoading(true);
    setIsOpen(true);
    try {
      const res = await apiClient.get('/search', { params: { q, per_page: 5 } });
      setResults(res.data);
    } catch (error) {
      console.error('Search failed', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && query.trim()) {
      setIsOpen(false);
      navigate(`/search?q=${encodeURIComponent(query)}`);
    }
  };

  const handleResultClick = async (type: string, id: number, doc?: any) => {
    if (type === 'document') {
      if ((doc?.match_type === 'ocr' || doc?.match_type === 'index') && doc?.matched_volume_id) {
        setIsOpen(false);
        setQuery('');
        const pageHash = doc.matched_page ? `#page=${doc.matched_page}` : '';
        navigate(`/documents/viewer/${doc.matched_volume_id}${pageHash}`);
        return;
      }
      setIsOpen(false);
      setQuery('');
      navigate(`/documents/${id}`);
    }
    if (type === 'folder') {
      setIsOpen(false);
      setQuery('');
      navigate(`/folders/${id}`);
    }
  };

  return (
    <div className="relative w-full max-w-5xl" ref={wrapperRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-slate-400" />
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-3 py-2 border border-slate-200 rounded-md leading-5 bg-slate-50 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors"
          placeholder="Search documents, folders..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => query.trim() && setIsOpen(true)}
        />
        {isLoading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <Loader2 className="h-4 w-4 text-slate-400 animate-spin" />
          </div>
        )}
      </div>

      {isOpen && query.trim() && !isLoading && (
        <div className="absolute mt-1 w-full md:w-[130%] lg:w-[150%] left-0 bg-white rounded-md shadow-lg border border-slate-200 z-50 overflow-hidden">
          <div className="max-h-96 overflow-y-auto py-2">
            
            {results.documents?.length > 0 && (
              <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">Documents</div>
            )}
            {results.documents.map((doc: any, idx: number) => (
              <button
                key={`${doc.document_id}-${doc.match_type}-${doc.matched_page}-${idx}`}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors focus:bg-slate-50 focus:outline-none flex items-center gap-3"
                onClick={() => handleResultClick('document', doc.document_id, doc)}
              >
                <div className="flex items-center gap-3 w-full">
                  <FileText className="h-4 w-4 text-indigo-500 shrink-0" />
                  <div className="flex-1 truncate flex items-center justify-between">
                    <span className="truncate">
                      {doc.matched_volume_name ? (
                        <>
                          <span className="font-semibold text-slate-900">{doc.title}</span>
                          <span className="text-slate-600 text-xs ml-1 font-medium">
                            ({doc.matched_volume_name}
                            {doc.matched_volume_start_page && doc.matched_volume_end_page ? `: ${doc.matched_volume_start_page}-${doc.matched_volume_end_page}` : ''})
                          </span>
                          <span className="text-slate-500 text-xs ml-1">
                            {doc.matched_index_title && !doc.matched_index_title.toLowerCase().startsWith('pdf page')
                              ? `- ${doc.matched_index_title} (Page ${doc.matched_page})`
                              : doc.matched_page ? `- Page ${doc.matched_page}` : ''}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="font-semibold text-slate-900">{doc.title}</span>
                          {doc.matched_page && (
                            <span className="text-slate-500 text-xs ml-1">
                              {doc.matched_index_title && !doc.matched_index_title.toLowerCase().startsWith('pdf page')
                                ? `- ${doc.matched_index_title} (Page ${doc.matched_page})`
                                : `- Page ${doc.matched_page}`}
                            </span>
                          )}
                        </>
                      )}
                    </span>
                    {doc.match_type === 'ocr' && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200 ml-2 whitespace-nowrap">
                        OCR Match
                      </span>
                    )}
                    {doc.match_type === 'index' && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-purple-50 text-purple-600 border border-purple-200 ml-2 whitespace-nowrap">
                        📑 Index Match
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}

            {results.folders?.length > 0 && (
              <div className="px-3 py-1.5 mt-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Folders</div>
            )}
            {results.folders?.slice(0, 3).map((folder: any) => (
              <button
                key={`folder-${folder.folder_id}`}
                className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-3"
                onClick={() => handleResultClick('folder', folder.folder_id)}
              >
                <Folder className="h-4 w-4 text-indigo-400 shrink-0" />
                <span className="truncate">{folder.name}</span>
              </button>
            ))}

            {results.users?.length > 0 && (
              <div className="px-3 py-1.5 mt-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">Users</div>
            )}
            {results.users?.slice(0, 3).map((u: any) => (
              <button
                key={`user-${u.user_id}`}
                className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 flex items-center gap-3"
                // onClick={() => handleResultClick('user', u.user_id)}
              >
                <User className="h-4 w-4 text-slate-400 shrink-0" />
                <span className="truncate">{u.first_name} {u.last_name}</span>
              </button>
            ))}

            {!results.documents?.length && !results.folders?.length && !results.users?.length && (
              <div className="px-4 py-3 text-sm text-slate-500 text-center">No results found</div>
            )}

            <div className="border-t border-slate-100 mt-2">
              <button 
                className="w-full text-center px-4 py-3 text-sm text-indigo-600 hover:bg-slate-50 font-medium"
                onClick={() => {
                  setIsOpen(false);
                  navigate(`/search?q=${encodeURIComponent(query)}`);
                }}
              >
                See all results for "{query}"
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
